"use client";

import { useEffect, useRef } from "react";
import type { AgenticCartBundleSummaryResponse } from "@ishvaram/api-client";
import { WhatsappOptInCheckbox } from "@/components/WhatsappOptInCheckbox";
import { forgetCartConfirmation } from "./AgenticCartCheckout.confirmation";
import type {
  CartRecoveryState,
  CartStatus,
  ContactForm,
  SuccessState,
} from "./AgenticCartCheckout.types";

type Language = "en" | "hi";
const fields: {
  key: keyof ContactForm;
  type: string;
  en: string;
  hi: string;
  autoComplete?: string;
}[] = [
  {
    key: "name",
    type: "text",
    en: "Name for report",
    hi: "रिपोर्ट के लिए नाम",
    autoComplete: "name",
  },
  {
    key: "birthPlace",
    type: "text",
    en: "Birth place",
    hi: "जन्म स्थान",
    autoComplete: "address-level2",
  },
  { key: "birthDate", type: "date", en: "Birth date", hi: "जन्म तिथि" },
  { key: "birthTime", type: "time", en: "Birth time (optional)", hi: "जन्म समय (वैकल्पिक)" },
  { key: "email", type: "email", en: "Email", hi: "ईमेल", autoComplete: "email" },
  { key: "phone", type: "tel", en: "WhatsApp", hi: "WhatsApp नंबर", autoComplete: "tel" },
];

const hindiErrors: Record<string, string> = {
  "This payment was refunded. You can start a new payment.":
    "यह भुगतान वापस कर दिया गया है। आप नया भुगतान शुरू कर सकते हैं।",
  "Payment confirmation is pending. Do not pay again. Contact support.":
    "भुगतान की पुष्टि बाकी है। दोबारा भुगतान न करें। सहायता लें।",
  "Name is required for the report.": "रिपोर्ट के लिए नाम भरें।",
  "Birth date and birth place are required for the report.":
    "रिपोर्ट के लिए जन्म तिथि और जन्म स्थान भरें।",
  "Email and WhatsApp are required for delivery and support.":
    "रिपोर्ट और सहायता के लिए ईमेल और WhatsApp नंबर भरें।",
  "Enter a valid email.": "सही ईमेल पता भरें।",
  "Payment failed. Please try again or use another method.":
    "भुगतान नहीं हुआ। फिर कोशिश करें या दूसरा भुगतान तरीका चुनें।",
  "Could not start checkout. Try again.": "भुगतान शुरू नहीं हो सका। फिर कोशिश करें।",
};

function bundleName(bundle: AgenticCartBundleSummaryResponse, hi: boolean) {
  return hi && bundle.bundle_id === "complete_reading_plan" ? "संपूर्ण रीडिंग" : bundle.name;
}

type DigitalCheckoutProps = {
  bundle: AgenticCartBundleSummaryResponse;
  contact: ContactForm;
  status: CartStatus;
  error: string;
  language: Language;
  onLanguageChange: (language: Language) => void;
  onContactChange: (field: keyof ContactForm, value: string) => void;
  onPay: () => Promise<void>;
  onCheckConfirmation?: () => void;
  recovery: CartRecoveryState | null;
  whatsappConsent: boolean;
  onWhatsappConsentChange: (value: boolean) => void;
};

export function DigitalCartCheckout({
  bundle,
  contact,
  status,
  error,
  language,
  onLanguageChange,
  onContactChange,
  onPay,
  onCheckConfirmation,
  recovery,
  whatsappConsent,
  onWhatsappConsentChange,
}: DigitalCheckoutProps) {
  const hi = language === "hi";
  const pending = status === "verifying" || status === "verification_pending";
  const busy = status === "paying" || pending;
  const complete = bundle.bundle_id === "complete_reading_plan";
  return (
    <div
      role="region"
      translate="no"
      aria-labelledby="digital-checkout-heading"
      lang={language}
      className="min-h-screen bg-background px-4 py-6 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-3xl rounded-lg border border-ish-border bg-ish-bg-card p-5 sm:p-6">
        <h1 id="digital-checkout-heading" className="font-serif text-2xl font-bold text-ish-dark">
          {bundleName(bundle, hi)}
        </h1>
        <p className="mt-3 text-sm leading-6 text-ish-muted">
          {complete
            ? hi
              ? "बृहत् कुंडली, दोष निदान और उपाय योजना, तथा वार्षिक पूर्वानुमान — तीन रीडिंग एक साथ।"
              : "Brihat Kundali, Dosha Diagnosis & Remedy Plan, and Annual Forecast — three readings together."
            : bundle.explanation}
        </p>
        <p className="mt-3 text-xl font-semibold text-ish-dark">
          {hi ? "कुल" : "Total"}: ₹{bundle.price_inr}
        </p>
        <label className="mt-5 block text-sm font-semibold text-ish-dark">
          <span>{hi ? "रिपोर्ट की भाषा" : "Report language"}</span>
          <select
            value={language}
            disabled={busy}
            onChange={(event) => onLanguageChange(event.target.value === "hi" ? "hi" : "en")}
            className="input-sacred mt-1 block w-full rounded-md px-3 py-2"
          >
            <option value="en">English</option>
            <option value="hi">हिंदी</option>
          </select>
        </label>
        <DigitalCheckoutForm
          contact={contact}
          status={status}
          error={error}
          language={language}
          onContactChange={onContactChange}
          onPay={onPay}
          onCheckConfirmation={onCheckConfirmation}
          whatsappConsent={whatsappConsent}
          onWhatsappConsentChange={onWhatsappConsentChange}
        />
        <p className="mt-4 text-xs text-ish-muted">
          {hi
            ? "Razorpay से सुरक्षित भुगतान · UPI और कार्ड"
            : "Secure payment via Razorpay · UPI and cards"}
        </p>
        <p className="mt-2 text-sm text-ish-muted">
          {hi
            ? "भुगतान की पुष्टि के बाद रिपोर्ट तैयार होती हैं। अपनी रीडिंग के लिंक सुरक्षित रखें।"
            : "Your readings are prepared after payment confirmation. Keep your reading links so you can return later."}
        </p>
        {recovery && (
          <p className="mt-4 text-sm text-ish-dark">
            {hi
              ? "भुगतान पूरा नहीं हुआ। विवरण जाँचकर फिर कोशिश करें।"
              : "Checkout was not completed. Check your details and try again."}{" "}
            <a
              href={recovery.resumeHref}
              className="inline font-semibold text-intent-brand underline"
            >
              {hi ? "भुगतान फिर खोलें" : "Resume checkout"}
            </a>
          </p>
        )}
        <a
          href={recovery?.supportHref || "/contact/"}
          className="mt-4 inline-block text-sm font-semibold text-intent-brand underline"
        >
          {hi ? "सहायता लें" : "Get help"}
        </a>
      </div>
    </div>
  );
}

function DigitalCheckoutForm({
  onCheckConfirmation,
  contact,
  status,
  error,
  language,
  onContactChange,
  onPay,
  whatsappConsent,
  onWhatsappConsentChange,
}: Pick<
  DigitalCheckoutProps,
  | "contact"
  | "status"
  | "error"
  | "language"
  | "onContactChange"
  | "onPay"
  | "onCheckConfirmation"
  | "whatsappConsent"
  | "onWhatsappConsentChange"
>) {
  const hi = language === "hi";
  const busy = status !== "ready";
  return (
    <form
      translate="no"
      onSubmit={(event) => {
        event.preventDefault();
        void onPay();
      }}
      noValidate
    >
      <fieldset disabled={busy} className="mt-5 grid gap-4 sm:grid-cols-2">
        <legend className="sr-only">
          {hi ? "जन्म और संपर्क विवरण" : "Birth and contact details"}
        </legend>
        {fields.map((field) => (
          <label key={field.key} className="block text-sm font-semibold text-ish-dark">
            <span>{hi ? field.hi : field.en}</span>
            <input
              name={field.key}
              type={field.type}
              autoComplete={field.autoComplete}
              value={contact[field.key]}
              required={field.key !== "birthTime"}
              onChange={(event) => onContactChange(field.key, event.target.value)}
              className="input-sacred mt-1 w-full rounded-md px-3 py-2 text-sm text-ish-text"
            />
          </label>
        ))}
      </fieldset>
      <p className="mt-3 text-xs text-ish-muted">
        {hi
          ? "जन्म समय नहीं जानते तो खाली छोड़ें; समय पर निर्भर निष्कर्ष सीमित हो सकते हैं।"
          : "If you do not know your birth time, leave it blank; time-dependent interpretations may be limited."}
      </p>
      {contact.phone.trim() && (
        <div className="mt-4">
          <WhatsappOptInCheckbox
            checked={whatsappConsent}
            onChange={onWhatsappConsentChange}
            language={language}
            disabled={busy}
          />
        </div>
      )}
      <DigitalPaymentState
        status={status}
        error={error}
        language={language}
        onCheckConfirmation={onCheckConfirmation}
      />
    </form>
  );
}

function DigitalPaymentState({
  status,
  error,
  language,
  onCheckConfirmation,
}: Pick<DigitalCheckoutProps, "status" | "error" | "language" | "onCheckConfirmation">) {
  const errorRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);
  const hi = language === "hi";
  const busy = status !== "ready";
  const labels = paymentStateLabels(status, hi, error);
  return (
    <>
      {error && (
        <p
          role="alert"
          ref={errorRef}
          tabIndex={-1}
          className="mt-4 rounded-md border border-error/20 bg-error/10 px-3 py-2 text-sm text-error"
        >
          {labels.error}
        </p>
      )}
      <button
        type="submit"
        disabled={busy}
        className="btn-sacred mt-5 w-full rounded-lg px-5 py-3 text-sm font-bold"
      >
        {labels.button}
      </button>
      {status === "verification_pending" && onCheckConfirmation && (
        <button
          type="button"
          onClick={onCheckConfirmation}
          className="mt-4 text-sm font-semibold text-intent-brand underline"
        >
          {labels.retry}
        </button>
      )}
      <p role="status" className="sr-only">
        {labels.status}
      </p>
    </>
  );
}

function paymentStateLabels(status: CartStatus, hi: boolean, error: string) {
  const pending = status === "verifying" || status === "verification_pending";
  const busy = status !== "ready";
  const message = pending
    ? hi
      ? "भुगतान की पुष्टि बाकी है"
      : "Awaiting payment confirmation"
    : hi
      ? "भुगतान खुल रहा है"
      : "Opening payment";
  return {
    status: busy ? message : "",
    button: busy
      ? `${message}${pending ? "" : "…"}`
      : hi
        ? "सुरक्षित भुगतान करें"
        : "Continue to secure payment",
    retry: hi ? "भुगतान की पुष्टि फिर जांचें" : "Check payment confirmation again",
    error: hi
      ? (hindiErrors[error] ?? `भुगतान आगे नहीं बढ़ सका। सहायता लें या फिर कोशिश करें। ${error}`)
      : error,
  };
}

export function DigitalCartSuccess({
  bundle,
  success,
  language,
}: {
  bundle: AgenticCartBundleSummaryResponse;
  success: SuccessState;
  language: Language;
}) {
  const hi = language === "hi";
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  return (
    <div
      role="region"
      translate="no"
      aria-labelledby="digital-payment-heading"
      lang={language}
      className="min-h-screen bg-background px-4 py-6"
    >
      <div className="mx-auto max-w-3xl rounded-lg border border-ish-border bg-ish-bg-card p-6">
        <h1
          ref={headingRef}
          tabIndex={-1}
          id="digital-payment-heading"
          className="font-serif text-2xl font-bold text-ish-dark"
        >
          {hi ? "भुगतान की पुष्टि हो गई" : "Payment confirmed"}
        </h1>
        <p className="mt-3 font-semibold text-ish-dark">{bundleName(bundle, hi)}</p>
        <p role="status" className="mt-3 text-sm text-ish-muted">
          {hi
            ? "आपकी रीडिंग तैयार होने पर उपलब्ध होंगी। भुगतान दोबारा न करें। कोई रीडिंग न मिले तो सहायता लें।"
            : "Your readings become available as they are prepared. Do not pay again. If a reading is missing, contact support."}
        </p>
        <p className="mt-3 text-sm text-ish-muted">
          {hi ? "ऑर्डर संदर्भ" : "Order reference"}: {success.order.cart_id}
        </p>
        {success.result.download_url && (
          <a
            href={success.result.download_url}
            className="btn-sacred mt-5 inline-flex rounded-lg px-5 py-3 text-sm font-bold"
          >
            {hi ? "अपनी रीडिंग खोलें" : "Open your reading"}
          </a>
        )}
        {!success.result.download_url && (
          <p className="mt-3 text-sm text-ish-muted">
            {hi
              ? "निजी रीडिंग लिंक के लिए अपना ईमेल देखें। लिंक न मिले तो ऊपर दिए ऑर्डर संदर्भ के साथ सहायता लें।"
              : "Check your email for your private reading link. If it has not arrived, contact support with the order reference above."}
          </p>
        )}
        <a
          href="/contact/"
          className="mt-4 block text-sm font-semibold text-intent-brand underline"
        >
          {hi ? "रिपोर्ट के लिए सहायता लें" : "Get help with your readings"}
        </a>
        <a
          href={`/cart/?bundle_id=${encodeURIComponent(bundle.bundle_id)}&lang=${language}`}
          onClick={forgetCartConfirmation}
          className="mt-4 block text-sm font-semibold text-intent-brand underline"
        >
          {hi ? "किसी और के लिए नई रीडिंग शुरू करें" : "Start a new reading for someone else"}
        </a>
      </div>
    </div>
  );
}
